import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';

/**
 * Pick the route an operator should land on, given their roles.
 * super_admin / sales → /dashboard; data_manager → /entities;
 * social_manager → /social; none → /no-access.
 * Mirrors the role taxonomy in the backend's api/admin/operators.py.
 */
export function landingRouteForRoles(roles: string[]): string {
  if (roles.includes('super_admin')) return '/dashboard';
  if (roles.includes('sales')) return '/dashboard';
  if (roles.includes('data_manager')) return '/entities';
  if (roles.includes('social_manager')) return '/social';
  return '/no-access';
}

/**
 * Route guard for role-restricted pages. Used as a layout route — children
 * render via <Outlet />. If the operator doesn't have the required role
 * (and isn't a super_admin, which always passes), redirects to their
 * landing route.
 *
 * Assumes the route tree already wraps this inside ProtectedRoute, so a
 * Firebase user is guaranteed present.
 */
export function RequireRole({ roles }: { roles: string | string[] }) {
  const { operator, loading, hasRole } = useAuth();
  const required = Array.isArray(roles) ? roles : [roles];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-100 border-r-transparent"></div>
          <p className="mt-4 text-grey-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Operator profile failed to load — backend likely rejected (account
  // disabled, etc.). Route to /no-access rather than the requested page.
  if (!operator) {
    return <Navigate to="/no-access" replace />;
  }

  if (!required.some((r) => hasRole(r))) {
    return <Navigate to={landingRouteForRoles(operator.roles)} replace />;
  }

  return <Outlet />;
}
